import { useContext, useEffect, useState } from 'react'

import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import {
  fetchComments,
  resetComments,
  setIsThreadsLoading,
  setPermissionSet,
} from '@editor/pages/projects/common/Comments/store/comments'
import { decideActionPermission } from '@editor/services/Validation'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'

export const useComments = () => {
  // Route matching to check current page
  const { project_id } = useParams<{ project_id: string }>()
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Contexts
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const dispatch = useAppDispatch()
  const isPageLoading = useSelector((state: RootState) => state.page.isLoading)
  const isInvited = useSelector((state: RootState) => state.page.isInvited)
  const isOwner = useSelector((state: RootState) => state.page.isOwner)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)

  const isLoading = useSelector((state: RootState) => state.comments.isThreadsLoading)

  // States
  const [initCompleted, setInitCompleted] = useState(false)

  /**
   * On page load, fetch comments
   */
  useEffect(() => {
    if (isPageLoading || !project_id || !inspectionArea || initCompleted) {
      return
    }

    void (async () => {
      dispatch(setIsThreadsLoading(true))

      // Reset first to prevent any residue from another project/inspection area
      dispatch(resetComments())

      const access_token = await getAccessToken()
      if (!access_token) {
        return
      }

      await dispatch(
        fetchComments({
          access_token,
          project_id,
          inspection_area_id: inspectionArea.inspection_area_id || '',
          showErrorModal,
        }),
      )

      setInitCompleted(true)
    })().finally(() => {
      dispatch(setIsThreadsLoading(false))
    })
  }, [project_id, inspectionArea, initCompleted, isPageLoading, getAccessToken, showErrorModal, dispatch])

  /**
   * Reset initComplete when inspection area changes
   */
  useEffect(() => {
    if (inspectionArea?.inspection_area_id !== inspection_area_id) {
      setInitCompleted(false)
    }
  }, [inspectionArea, inspection_area_id])

  /**
   * Get permission set for the Comments
   */
  useEffect(() => {
    if (isPageLoading) return

    dispatch(setPermissionSet(decideActionPermission(isOwner, isInvited).COMMENT_THREAD))
  }, [isPageLoading, isInvited, isOwner, dispatch])

  return { isLoading }
}
